'use client';
import { FC } from 'react';
import Typography from '@egalite/ui/Typography';

const reasons = [
    {
        number: '01',
        title: 'Conexão direta com uma das maiores comunidades de profissionais com deficiência do Brasil',
        description: 'Ao longo das edições do evento e da construção da plataforma, a IncluiPcD reuniu milhares de profissionais em busca de oportunidades reais. Isso gerou uma comunidade sólida, engajada e que confia na marca.',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        )
    },
    {
        number: '02',
        title: 'Experiência validada por grandes empresas de diferentes setores',
        description: 'Diversas empresas de tecnologia, saúde, educação, serviços e indústria já utilizaram a IncluiPcD para divulgar vagas e fortalecer suas ações de inclusão. A plataforma atende tanto quem está começando quanto organizações com programas estruturados.',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>
        )
    },
    {
        number: '03',
        title: 'Conteúdos que formam, inspiram e ajudam sua empresa a evoluir',
        description: 'As conversas, trilhas e materiais produzidos ao longo das edições do evento e da plataforma ajudam empresas a compreenderem caminhos práticos para avançar na inclusão de forma consistente.',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
        )
    },
    {
        number: '04',
        title: 'Ferramentas criadas a partir de necessidades reais do mercado',
        description: 'A plataforma foi desenvolvida ouvindo empresas e profissionais ao longo de várias edições. Ela é simples, intuitiva e nasce de dores reais de quem recruta e de quem busca oportunidades.',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
        )
    },
    {
        number: '05',
        title: 'Divulgação estratégica que amplia o alcance das suas vagas',
        description: 'A IncluiPcD utiliza comunidade, redes sociais, e-mail e WhatsApp para dar visibilidade às oportunidades da sua empresa. Isso garante alcance real e engajamento maior do que o obtido em portais tradicionais.',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
        )
    }
];

const WhyChooseUs: FC = () => {
    return (
        <section 
            className="pt-[80px] pb-[80px] relative overflow-hidden"
            style={{
                backgroundImage: `url('/office.jpeg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            {/* Dark Blue Overlay */}
            <div className="absolute inset-0 bg-[#0A1647]/90" />

            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
                <h2 className="text-white text-[36px] font-bold mb-[64px]">
                    Por que contratar a IncluiPcD?
                </h2>
                
                {/* Responsive grid for top row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                    {reasons.slice(0, 3).map((reason, idx) => (
                        <div 
                            key={idx} 
                            className="bg-white rounded-[16px] p-[24px] text-left shadow-lg flex flex-col items-start transition-all duration-300 hover:shadow-xl"
                        >
                            <div className="flex items-center gap-4 mb-[24px]">
                                <div className="w-[48px] h-[48px] rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#2563EB]">
                                    {reason.icon}
                                </div>
                                <span className="text-[#2563EB] text-[16px] font-bold">
                                    {reason.number}
                                </span>
                            </div>
                            <h3 className="text-[#111827] text-[18px] font-bold mb-[12px]">
                                {reason.title}
                            </h3>
                            <p className="text-[#4B5563] text-[14px] leading-relaxed">
                                {reason.description}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Bottom row centered for 2 cards */}
                <div className="flex flex-col md:flex-row justify-center gap-8">
                    {reasons.slice(3, 5).map((reason, idx) => (
                        <div 
                            key={idx} 
                            className="bg-white rounded-[16px] p-[24px] text-left shadow-lg flex flex-col items-start transition-all duration-300 hover:shadow-xl w-full md:max-w-[calc(50%-16px)] lg:max-w-[368px]"
                        >
                            <div className="flex items-center gap-4 mb-[24px]">
                                <div className="w-[48px] h-[48px] rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#2563EB]">
                                    {reason.icon}
                                </div>
                                <span className="text-[#2563EB] text-[16px] font-bold">
                                    {reason.number}
                                </span>
                            </div>
                            <h3 className="text-[#111827] text-[18px] font-bold mb-[12px]">
                                {reason.title}
                            </h3>
                            <p className="text-[#4B5563] text-[14px] leading-relaxed">
                                {reason.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default WhyChooseUs;
